import {
    // eslint-disable-next-line no-restricted-imports
    useQuery as _useQuery,
    // eslint-disable-next-line no-restricted-imports
    useQueries as _useQueries,
    useQueryClient,
    UseQueryOptions,
    QueryKey,
} from "@tanstack/react-query";
import { HttpMethod, THttpMethod } from "src/constants/http-method";
import { MAX_ATTEMPTS, RETRY_DELAY_MS } from "src/constants/retry";
import { buildQueryUrl } from "src/utils/query-util";
import { useRouterLocale } from "src/utils/router/use-router-locale";

const NO_CONTENT_STATUS = 204;

const readResponseValue = async (response: Response, isGet: boolean) => {
    if (response.status === NO_CONTENT_STATUS || response.headers.get("Content-Length") === "0") {
        return null;
    }
    if (isGet) {
        return response.json();
    }
    return response.json().catch(() => null);
};

interface ICreateQueryFnParams<TData> {
    url: string;
    method: THttpMethod;
    body?: unknown;
    transformResponse?: (response: unknown) => TData;
}

const createQueryFn =
    <TData>({ url, method, body, transformResponse }: ICreateQueryFnParams<TData>) =>
    async ({ signal }: { signal: AbortSignal }): Promise<TData> => {
        const isGet = method === HttpMethod.GET;

        const response = await fetch(isGet ? url.toLowerCase() : url, {
            method,
            headers: { Accept: "application/json", ...(!isGet && { "Content-Type": "application/json" }) },
            ...(!isGet && { body: JSON.stringify(body ?? {}) }),
            signal,
        });

        const responseValue = await readResponseValue(response, isGet);

        if (!response.ok) {
            throw responseValue ?? new Error(response.statusText);
        }

        return transformResponse ? transformResponse(responseValue) : (responseValue as TData);
    };

/**
 * Options that components can pass into API hooks.
 * Does not include `url` or `queryKey` — those are determined by the API hook itself.
 */
export interface IQueryOptions<TData, TError = Error> extends Omit<UseQueryOptions<TData, TError, TData, QueryKey>, "queryKey"> {
    queryParams?: URLSearchParams;
    method?: THttpMethod;
    body?: unknown;
    transformResponse?: (response: unknown) => TData;
}

/**
 * Full input shape for the `useQuery` wrapper.
 * Extends `IQueryOptions` with the required `url` and an optional semantic `queryKey`.
 *
 * When `queryKey` is omitted the built URL is used as the cache key.
 */
export interface IUseQueryProps<TData, TError = Error> extends IQueryOptions<TData, TError> {
    url: string;
    queryKey?: QueryKey;
}

export const useQuery = <TData = unknown, TError = Error>({
    url: _url,
    queryKey: customQueryKey,
    queryParams,
    method,
    body,
    transformResponse,
    ...options
}: IUseQueryProps<TData, TError>) => {
    const { language, country } = useRouterLocale();

    const url = buildQueryUrl({ language, country }, _url, queryParams);
    const cacheKey: QueryKey = customQueryKey ?? [url];

    const queryClient = useQueryClient();
    const result = _useQuery<TData, TError, TData, QueryKey>({
        queryKey: cacheKey,
        queryFn: createQueryFn<TData>({ url, method: method ?? HttpMethod.GET, body, transformResponse }),
        retry: MAX_ATTEMPTS,
        retryDelay: RETRY_DELAY_MS,
        ...options,
    });

    return {
        ...result,
        setQueryData: (data: TData) => queryClient.setQueryData(cacheKey, data),
        invalidate: () => queryClient.invalidateQueries({ queryKey: cacheKey }),
    };
};

/**
 * Multi-query variant. Each entry follows the same flat `IUseQueryProps` shape —
 * no nested `options` object, `url` and `queryKey` sit alongside the standard options.
 */
export const useQueries = <TData, TError = Error>(queries: IUseQueryProps<TData, TError>[]) => {
    const { language, country } = useRouterLocale();

    return _useQueries({
        queries: queries.map(({ url: _url, queryKey: customQueryKey, queryParams, method, body, transformResponse, ...options }) => {
            const url = buildQueryUrl({ language, country }, _url, queryParams);
            return {
                queryKey: customQueryKey ?? [url],
                queryFn: createQueryFn({ url, method: method ?? HttpMethod.GET, body, transformResponse }),
                retry: MAX_ATTEMPTS,
                retryDelay: RETRY_DELAY_MS,
                ...options,
            };
        }),
    });
};
